<template>
  <teleport to="body">
    <!-- 遮罩层 -->
    <transition name="fade">
      <div 
        v-if="show" 
        class="image-preview-overlay"
        @click="handleClose"
      ></div>
    </transition>
    
    <!-- 图片预览弹窗 -->
    <transition name="zoom">
      <div v-if="show" class="image-preview-container" @click="handleClose">
        <div class="image-preview-content" @click.stop>
          <div class="image-preview-header">
            <h3 class="preview-title">{{ title }}</h3>
            <button class="preview-close-btn" @click="handleClose">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <div class="image-preview-body">
            <img 
              :src="$fixAssetUrl(imageUrl)" 
              :alt="title"
              class="preview-image"
              @error="handleImageError"
            />
            <div class="preview-actions">
              <button class="action-btn download-btn" @click="handleDownload">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 13V3M10 13L6 9M10 13L14 9M3 16H17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>保存图片</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { watch, getCurrentInstance } from 'vue'

/**
 * 图片预览组件
 * 支持放大显示和下载功能
 */
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  imageUrl: {
    type: String,
    required: true
  },
  title: {
    type: String,
    default: '图片预览'
  }
})

const emit = defineEmits(['update:show', 'close'])

/**
 * 获取当前实例以访问全局属性
 */
const instance = getCurrentInstance()
const fixAssetUrl = instance?.appContext?.config?.globalProperties?.$fixAssetUrl || ((url) => url)

/**
 * 关闭预览
 */
const handleClose = () => {
  emit('update:show', false)
  emit('close')
}

/**
 * 图片加载错误处理
 */
const handleImageError = (e) => {
  console.error('Preview image load error:', e.target.src)
}

/**
 * 下载图片
 */
const handleDownload = async () => {
  if (typeof window === 'undefined') return
  
  try {
    // 使用 fixAssetUrl 处理图片路径
    let imageUrl = fixAssetUrl(props.imageUrl)
    
    // 如果是相对路径，转换为绝对路径
    if (!imageUrl.startsWith('http') && !imageUrl.startsWith('//')) {
      imageUrl = window.location.origin + (imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl)
    }
    
    const response = await fetch(imageUrl)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    
    const link = document.createElement('a')
    link.href = url
    link.download = `${props.title || 'qrcode'}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // 清理 URL
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Download failed:', error)
    // 降级方案：直接打开图片
    let imageUrl = fixAssetUrl(props.imageUrl)
    if (!imageUrl.startsWith('http') && !imageUrl.startsWith('//')) {
      imageUrl = window.location.origin + (imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl)
    }
    window.open(imageUrl, '_blank')
  }
}

/**
 * 监听 show 变化，控制 body 滚动
 */
watch(() => props.show, (newVal) => {
  if (typeof window !== 'undefined' && document) {
    if (newVal) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
  }
}, { immediate: true })
</script>

<style scoped lang="scss">
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 3000;
}

.image-preview-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3001;
  padding: 20px;
  box-sizing: border-box;
}

.image-preview-content {
  background-color: #fff;
  border-radius: 16px;
  max-width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  
  .image-preview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;
    
    .preview-title {
      font-size: 18px;
      font-weight: 600;
      color: #333;
      margin: 0;
    }
    
    .preview-close-btn {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      background: transparent;
      cursor: pointer;
      color: #666;
      padding: 0;
      transition: color 0.2s;
      
      &:hover {
        color: #333;
      }
      
      svg {
        width: 20px;
        height: 20px;
      }
    }
  }
  
  .image-preview-body {
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: auto;
    
    .preview-image {
      max-width: 100%;
      max-height: 60vh;
      height: auto;
      display: block;
      margin-bottom: 20px;
    }
    
    .preview-actions {
      display: flex;
      justify-content: center;
      gap: 12px;
      
      .action-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 24px;
        border-radius: 8px;
        border: none;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.2s;
        
        &.download-btn {
          background-color: #1989fa;
          color: #fff;
          
          &:hover {
            background-color: #0570c9;
          }
          
          &:active {
            background-color: #0468b8;
          }
        }
        
        svg {
          width: 18px;
          height: 18px;
        }
      }
    }
  }
}

// 淡入淡出动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// 缩放动画
.zoom-enter-active,
.zoom-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.zoom-enter-from,
.zoom-leave-to {
  transform: scale(0.8);
  opacity: 0;
}
</style>
